/**
 * Бланки додатків декларації з податку на прибуток (ч.1388; ч.1389 — у вигляді ОФІЦІЙНИХ форм,
 * як декларація з ПДВ): АМ (амортизація), ЗП (зменшення податку), АВ (дивіденди), ПН
 * (нерезиденти — окремий додаток на кожного), ВП (виправлення помилок — на кожен період).
 * Похідні клітинки рахує сервер при збереженні; редагувані — жовті, похідні — сірі, друк — текстом.
 */
import type { CSSProperties } from 'react';
import type { Organization } from '@/api/catalogs';
import {
  MAIN_ROWS,
  type AppendixRowDef, type AppendixRows, type PnItem, type ProfitTaxDeclaration, type VpItem,
} from '@/api/profitTaxDeclarations';
import { B, SERIF, XMark, blankBox, nameCell, numCell, thBlank, valCell } from './VatDeclarationBlank';
import { AppendixHeader, Mark, PeriodMarks, SignaturesBlock, SumCell, TextCell, fmt0, label, ro, small, withRates } from './ProfitTaxBlank';

const PERIODS: Record<number, string> = { 1: 'I квартал', 2: 'півріччя', 3: 'три квартали', 4: 'рік' };
const xCell: CSSProperties = { ...valCell, textAlign: 'center', color: '#555', fontFamily: SERIF };
const X = <span style={{ display: 'block', textAlign: 'center' }}>Х</span>;

/* ----------------------------- АМ ----------------------------- */

/** Рядки ОЗ бланка АМ: групи без податкової амортизації (1, 10, 11, 13) — клітинки «Х». */
const AM_FA_LAYOUT: { row: string; group: number; field?: string; sub?: string }[] = [
  { row: 'А1', group: 1 }, { row: 'А2', group: 2, field: 'R0102' },
  { row: 'А3', group: 3, field: 'R0103' }, { row: 'А3.1', group: 3, field: 'R01031', sub: 'accelerated' },
  { row: 'А3.2', group: 3, field: 'R01032', sub: 'housing' },
  { row: 'А4', group: 4, field: 'R0104' }, { row: 'А4.1', group: 4, field: 'R01041', sub: 'accelerated' },
  { row: 'А5', group: 5, field: 'R0105' }, { row: 'А5.1', group: 5, field: 'R01051', sub: 'accelerated' },
  { row: 'А6', group: 6, field: 'R0106' }, { row: 'А7', group: 7, field: 'R0107' }, { row: 'А8', group: 8, field: 'R0108' },
  { row: 'А9', group: 9, field: 'R0109' }, { row: 'А9.1', group: 9, field: 'R01091', sub: 'accelerated' },
  { row: 'А10', group: 10 }, { row: 'А11', group: 11 }, { row: 'А12', group: 12, field: 'R0112' },
  { row: 'А13', group: 13 }, { row: 'А14', group: 14, field: 'R0114' }, { row: 'А15', group: 15, field: 'R0115' },
  { row: 'А16', group: 16, field: 'R0116' },
];
const AM_INVENTORY: { field: string; title: string }[] = [
  { field: 'R0301', title: 'група 3 (передавальні пристрої)' }, { field: 'R0302', title: 'група 4 (машини та обладнання)' },
  { field: 'R0303', title: 'група 5 (транспортні засоби)' }, { field: 'R0304', title: 'група 9 (інші основні засоби)' },
];

export function AmAppendix({ rows, am, setCell, readOnly, print, doc, org }: {
  rows: AppendixRowDef[]; am: Record<string, string>;
  setCell?: (code: string, raw: string) => void; readOnly?: boolean; print?: boolean;
  doc?: ProfitTaxDeclaration; org?: Organization;
}) {
  const editable = !print && !readOnly && !!setCell;
  const byField = new Map(rows.map((r) => [r.field, r]));
  const sum3 = (field: string | undefined, col: string, canEdit: boolean, lbl: string) => (
    <td style={valCell}>
      {field
        ? <SumCell value={am[`${field}${col}`]} readOnly={!canEdit} print={print} label={`${lbl} ${col}`} onChange={(raw) => setCell?.(`${field}${col}`, raw)} />
        : X}
    </td>
  );
  const head = (
    <thead>
      <tr>
        <th style={thBlank} rowSpan={2}>Код рядка</th>
        <th style={thBlank} rowSpan={2}>Номер групи</th>
        <th style={thBlank} colSpan={2}>Балансова вартість</th>
        <th style={thBlank} rowSpan={2}>Розрахована сума амортизації за звітний (податковий) період відповідно до пункту 138.3 статті 138 розділу ІІІ, пунктів 43 та 43-1 підрозділу 4 розділу ХХ Податкового кодексу України</th>
      </tr>
      <tr>
        <th style={thBlank}>на початок звітного (податкового) періоду</th>
        <th style={thBlank}>на кінець звітного (податкового) періоду</th>
      </tr>
      <tr>{['1', '2', '3', '4', '5'].map((n) => <th key={n} style={thBlank}>{n}</th>)}</tr>
    </thead>
  );
  const hy1 = am.HZY1 ? Number(am.HZY1) : null;
  return (
    <div>
      {doc && (
        <AppendixHeader doc={doc} org={org} print={print}
          title="Додаток АМ до Податкової декларації з податку на прибуток підприємств (рядок 1.2.1 АМ додатка РІ до рядка 03 РІ)"
          subtitle="Інформація щодо нарахованої амортизації" />
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
        <colgroup><col style={{ width: 70 }} /><col style={{ width: 70 }} /><col style={{ width: 140 }} /><col style={{ width: 140 }} /><col style={{ width: 170 }} /></colgroup>
        {head}
        <tbody>
          <tr><td colSpan={5} style={{ ...nameCell, textAlign: 'center', fontWeight: 700 }}>Основні засоби</td></tr>
          {AM_FA_LAYOUT.map((r) => {
            const def = r.field ? byField.get(r.field) : undefined;
            const canEdit = editable && !!r.field;
            return (
              <tr key={r.row}>
                <td style={numCell}>{r.row}{r.sub === 'accelerated' ? <sup>2</sup> : r.sub === 'housing' ? <sup>3</sup> : null}</td>
                <td style={{ ...numCell, fontWeight: 400 }} title={def?.title}>{r.group}</td>
                {r.field ? sum3(r.field, 'G3', canEdit, `АМ ${r.row}`) : <td style={xCell}>{X}</td>}
                {r.field ? sum3(r.field, 'G4', canEdit, `АМ ${r.row}`) : <td style={xCell}>{X}</td>}
                {r.field ? sum3(r.field, 'G5', canEdit, `АМ ${r.row}`) : <td style={xCell}>{X}</td>}
              </tr>
            );
          })}
          <tr style={{ fontWeight: 700 }}>
            <td style={numCell}>А17</td><td style={{ ...nameCell, fontSize: '0.72rem' }}>Сума рядків А1–А16</td>
            {sum3('R0117', 'G3', false, 'АМ А17')}{sum3('R0117', 'G4', false, 'АМ А17')}{sum3('R0117', 'G5', false, 'АМ А17')}
          </tr>
          <tr>
            <td style={numCell}>КП</td>
            <td style={{ ...nameCell, fontSize: '0.7rem' }}>Капіталізовані проценти, включені до собівартості необоротних активів (підпункт 138.3.2 пункту 138.3 статті 138 розділу ІІІ Податкового кодексу України)</td>
            {sum3('R0118', 'G3', editable, 'АМ КП')}{sum3('R0118', 'G4', editable, 'АМ КП')}{sum3('R0118', 'G5', editable, 'АМ КП')}
          </tr>
          <tr><td colSpan={5} style={{ ...nameCell, textAlign: 'center', fontWeight: 700 }}>Нематеріальні активи</td></tr>
          {[1, 2, 3, 4, 5, 6].map((n) => {
            const f = `R020${n}`;
            return (
              <tr key={f}>
                <td style={numCell}>Н{n}</td>
                <td style={{ ...numCell, fontWeight: 400 }} title={byField.get(f)?.title}>{Number(am[`${f}G3`] ?? 0) || Number(am[`${f}G4`] ?? 0) || Number(am[`${f}G5`] ?? 0) ? n : (print ? '' : n)}</td>
                {sum3(f, 'G3', editable, `АМ Н${n}`)}{sum3(f, 'G4', editable, `АМ Н${n}`)}{sum3(f, 'G5', editable, `АМ Н${n}`)}
              </tr>
            );
          })}
          <tr style={{ fontWeight: 700 }}>
            <td style={numCell}>Н7</td><td style={{ ...nameCell, fontSize: '0.72rem' }}>Сума рядків Н1–Н6</td>
            {sum3('R0207', 'G3', false, 'АМ Н7')}{sum3('R0207', 'G4', false, 'АМ Н7')}{sum3('R0207', 'G5', false, 'АМ Н7')}
          </tr>
          <tr style={{ fontWeight: 700 }}>
            <td style={numCell}>1.2.1<sup>1</sup></td>
            <td style={nameCell} colSpan={3}>Усього (рядок А17 + рядок Н7)</td>
            <td style={valCell}><SumCell value={am.R00121G3} readOnly print={print} /></td>
          </tr>
        </tbody>
      </table>
      <div style={{ ...label, marginTop: 6 }}>
        Звітний (податковий) період <span style={{ display: 'inline-block', minWidth: 54, borderBottom: B, textAlign: 'center', fontWeight: 700 }}>
          {editable ? <input value={am.HZY1 ?? ''} aria-label="Рік рішення про мінімальні строки" onChange={(e) => setCell?.('HZY1', e.target.value)} style={{ width: 54, border: 'none', background: '#fefce8', fontFamily: SERIF, textAlign: 'center' }} /> : (am.HZY1 ?? ' ')}
        </span> року, в якому прийнято рішення про застосування мінімально допустимих строків амортизації основних засобів, що були у використанні, введених в експлуатацію у період дії воєнного стану в Україні<sup>4</sup>
        <div style={{ marginTop: 3 }}>
          {([['H1KV1', 'І квартал'], ['HHY1', 'Півріччя'], ['H3KV1', 'Три квартали'], ['HY1', 'Рік']] as [string, string][]).map(([c, t]) => (
            <span key={c} style={{ marginRight: 14 }}>
              <Mark on={!!am[c]} readOnly={!editable} onToggle={() => setCell?.(c, am[c] ? '' : '1')} /> {t}
            </span>
          ))}
          {hy1 === null && !editable && <span style={small}> (рішення не приймалось)</span>}
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 6 }}>
        <thead>
          <tr><th style={thBlank} colSpan={3}>Інформація про результати інвентаризації обʼєктів основних засобів станом на 01 число податкового (звітного) періоду
            {' '}<span style={{ display: 'inline-block', minWidth: 54, borderBottom: B, fontWeight: 700 }}>
              {editable ? <input value={am.HZY2 ?? ''} aria-label="Рік інвентаризації" onChange={(e) => setCell?.('HZY2', e.target.value)} style={{ width: 54, border: 'none', background: '#fefce8', fontFamily: SERIF, textAlign: 'center' }} /> : (am.HZY2 ?? ' ')}
            </span> року, в якому прийнято рішення про застосування мінімально допустимих строків амортизації<sup>4</sup></th></tr>
          <tr><th style={thBlank}>Номер групи</th><th style={thBlank}>Балансова (залишкова) вартість на початок звітного (податкового) періоду</th><th style={thBlank}>на кінець звітного (податкового) періоду</th></tr>
        </thead>
        <tbody>
          {AM_INVENTORY.map((r) => (
            <tr key={r.field}>
              <td style={nameCell}>{r.title}</td>
              <td style={valCell}><SumCell value={am[`${r.field}G2`]} readOnly={!editable} print={print} label={`${r.title} початок`} onChange={(raw) => setCell?.(`${r.field}G2`, raw)} /></td>
              <td style={valCell}><SumCell value={am[`${r.field}G3`]} readOnly={!editable} print={print} label={`${r.title} кінець`} onChange={(raw) => setCell?.(`${r.field}G3`, raw)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ ...small, marginTop: 4 }}>
        <sup>1</sup> Значення графи 5 рядка 1.2.1 переноситься до рядка 1.2.1 АМ додатка РІ. <sup>2</sup> За прямолінійним методом з використанням мінімально
        допустимого строку (пункти 43, 43-1 підрозділу 4 розділу ХХ). <sup>3</sup> Житлові будинки для переміщених підприємств і працівників (пункт 69 підрозділу 4
        розділу ХХ). <sup>4</sup> У разі прийняття рішення про мінімально допустимі строки для основних засобів, що були у використанні.
      </div>
      {doc && <SignaturesBlock doc={doc} org={org} print={print} withInn={false} />}
    </div>
  );
}

/* ----------------------------- ЗП / АВ ----------------------------- */

export function SimpleAppendix({ rows, cells, setCell, readOnly, print, doc, org, title, subtitle, note }: {
  rows: AppendixRowDef[]; cells: Record<string, string>;
  setCell?: (code: string, raw: string) => void; readOnly?: boolean; print?: boolean;
  doc?: ProfitTaxDeclaration; org?: Organization; title?: string; subtitle?: string; note?: string;
}) {
  const editable = !print && !readOnly && !!setCell;
  return (
    <div>
      {doc && title && <AppendixHeader doc={doc} org={org} print={print} title={title} subtitle={subtitle} />}
      <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
        <colgroup><col /><col style={{ width: 70 }} /><col style={{ width: 150 }} /></colgroup>
        <thead>
          <tr><th style={thBlank}>ПОКАЗНИКИ</th><th style={thBlank}>Код рядка</th><th style={thBlank}>Сума</th></tr>
          <tr><th style={thBlank}>1</th><th style={thBlank}>2</th><th style={thBlank}>3</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.field} style={{ fontWeight: r.auto ? 700 : 400 }}>
              <td style={nameCell}>{withRates(r.title.replace(/× ставка/g, `× {${r.rate ?? 'R006G1'}}`), cells)}</td>
              <td style={numCell}>{r.row}</td>
              <td style={valCell}>
                <SumCell value={cells[r.field]} readOnly={!editable || !!r.auto} print={print} label={`${r.row} ${r.title}`}
                  onChange={(raw) => setCell?.(r.field, raw)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {note && <div style={{ ...small, marginTop: 4 }}>{note}</div>}
      {doc && <SignaturesBlock doc={doc} org={org} print={print} withInn={false} />}
    </div>
  );
}

/* ----------------------------- ПН ----------------------------- */

const EMPTY_PN: PnItem = { name: '', country: '', country_code: '', address: '', tin: '', rows: {} };

export function PnAppendix({ rows, items, setItems, readOnly, print, nonresidents, doc, org }: {
  rows: AppendixRows['pn']; items: PnItem[];
  setItems?: (items: PnItem[]) => void; readOnly?: boolean; print?: boolean;
  /** контрагенти-нерезиденти для швидкого заповнення шапки */
  nonresidents?: { id: string; name: string; full_name?: string | null; legal_address?: string | null; edrpou?: string | null; inn?: string | null }[];
  doc?: ProfitTaxDeclaration; org?: Organization;
}) {
  const editable = !print && !readOnly && !!setItems;
  const update = (i: number, patch: Partial<PnItem>) => setItems?.(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const setRow = (i: number, code: string, raw: string) => {
    const it = items[i];
    if (!it) return;
    const next = { ...it.rows };
    if (raw === '' || Number(raw) === 0) delete next[code]; else next[code] = raw;
    update(i, { rows: next });
  };
  const setBen = (i: number, patch: Record<string, unknown>) => update(i, { beneficiary: { ...(items[i]?.beneficiary ?? {}), ...patch } });
  const field = (i: number, key: keyof PnItem, lbl: string, align: 'left' | 'center' = 'left') => (
    <TextCell value={(items[i]?.[key] as string) ?? ''} readOnly={!editable} print={print} label={lbl} align={align}
      onChange={(v) => update(i, { [key]: key === 'country_code' ? v.replace(/\D/g, '').slice(0, 3) : v } as Partial<PnItem>)} />
  );
  return (
    <div>
      {items.length === 0 && <div style={{ ...label, color: '#555', marginBottom: 8 }}>Нерезидентів не додано — додаток ПН не подається.</div>}
      {items.map((it, i) => (
        <div key={i} className={print && i ? 'print-page' : undefined} style={{ marginBottom: '1rem' }}>
          {doc && (
            <AppendixHeader doc={doc} org={org} print={print} title={`Додаток ПН до рядка 23 ПН Податкової декларації з податку на прибуток підприємств${items.length > 1 ? ` (№ ${i + 1})` : ''}`} />
          )}
          {editable && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              {(nonresidents?.length ?? 0) > 0 && (
                <select value="" onChange={(e) => {
                  const c = nonresidents!.find((x) => x.id === e.target.value);
                  if (c) update(i, { name: c.full_name || c.name, address: c.legal_address ?? '', tin: c.edrpou || c.inn || it.tin });
                }} style={{ maxWidth: 360 }} aria-label="Заповнити з контрагента">
                  <option value="">— заповнити з контрагента-нерезидента —</option>
                  {nonresidents!.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              <button type="button" style={{ marginLeft: 'auto' }} onClick={() => setItems?.(items.filter((_, j) => j !== i))}>прибрати нерезидента</button>
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
            <tbody>
              <tr>
                <td style={{ ...nameCell, width: '50%' }}>Повне найменування нерезидента<sup>1</sup>{field(i, 'name', 'Повне найменування нерезидента')}</td>
                <td style={nameCell}>Назва країни резиденції нерезидента<sup>1</sup>{field(i, 'country', 'Назва країни резиденції')}</td>
              </tr>
              <tr>
                <td style={nameCell}>Місцезнаходження нерезидента<sup>1</sup>{field(i, 'address', 'Місцезнаходження нерезидента')}</td>
                <td style={nameCell}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: 8, alignItems: 'end' }}>
                    <span>Код країни резиденції<sup>1</sup></span>{field(i, 'country_code', 'Код країни резиденції', 'center')}
                  </div>
                </td>
              </tr>
              <tr>
                <td style={nameCell}>Код нерезидента в країні резиденції{field(i, 'tin', 'Код нерезидента в країні резиденції', 'center')}</td>
                <td style={nameCell}>
                  <div style={{ display: 'flex', gap: 16, ...label }}>
                    <span>Відмітка про наявність офшорного статусу<sup>2</sup> <Mark on={!!it.offshore} readOnly={!editable} onToggle={() => update(i, { offshore: !it.offshore })} /></span>
                    <span>Відмітка про відсутність статусу юридичної особи<sup>3</sup> <Mark on={!!it.no_legal_entity} readOnly={!editable} onToggle={() => update(i, { no_legal_entity: !it.no_legal_entity })} /></span>
                  </div>
                </td>
              </tr>
              {(editable || it.beneficiary?.name) && (
                <>
                  <tr>
                    <td style={nameCell}>Повне найменування нерезидента, який є бенефіціаром (фактичним) отримувачем (власником) доходу<sup>4</sup>
                      <TextCell value={it.beneficiary?.name ?? ''} readOnly={!editable} print={print} label="Бенефіціар — найменування" onChange={(v) => setBen(i, { name: v })} /></td>
                    <td style={nameCell}>Назва країни резиденції бенефіціара<sup>1</sup>
                      <TextCell value={it.beneficiary?.country ?? ''} readOnly={!editable} print={print} label="Бенефіціар — країна" onChange={(v) => setBen(i, { country: v })} /></td>
                  </tr>
                  <tr>
                    <td style={nameCell}>Місцезнаходження бенефіціара<sup>4</sup>
                      <TextCell value={it.beneficiary?.address ?? ''} readOnly={!editable} print={print} label="Бенефіціар — місцезнаходження" onChange={(v) => setBen(i, { address: v })} /></td>
                    <td style={nameCell}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <span>Код бенефіціара в країні резиденції<TextCell value={it.beneficiary?.tin ?? ''} readOnly={!editable} print={print} label="Бенефіціар — код" align="center" onChange={(v) => setBen(i, { tin: v })} /></span>
                        <span>Код країни резиденції<TextCell value={it.beneficiary?.country_code ?? ''} readOnly={!editable} print={print} label="Бенефіціар — код країни" align="center" onChange={(v) => setBen(i, { country_code: v.replace(/\D/g, '').slice(0, 3) })} /></span>
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
          <div style={{ ...label, textAlign: 'center', fontWeight: 700, margin: '6px 0 2px' }}>
            Таблиця 1. Розрахунок (звіт) податкових зобовʼязань нерезидентів, якими отримано доходи із джерелом їх походження з України<sup>5</sup>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
            <colgroup>
              <col /><col style={{ width: 44 }} /><col style={{ width: 110 }} /><col style={{ width: 62 }} /><col style={{ width: 70 }} />
              <col style={{ width: 100 }} /><col style={{ width: 110 }} /><col style={{ width: 100 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={thBlank}>Види доходів</th><th style={thBlank}>Код рядка</th><th style={thBlank}>Сума доходів</th>
                <th style={thBlank}>Ставка податку (%) згідно з Податковим кодексом України</th>
                <th style={thBlank}>Ставка податку (%) згідно з міжнародним договором<sup>6</sup></th>
                <th style={thBlank}>Сума податку (графа 3 × графу 4 / 100 або графа 3 × графу 5 / 100)</th>
                <th style={thBlank}>Сума податку до сплати при виплаті доходу у будь-якій формі, відмінній від грошової, або якщо податок не було утримано з доходу під час його виплати (графа 3 × 100 / (100 – графа 4) – графа 3) або (графа 3 × 100 / (100 – графа 5) – графа 3)</th>
                <th style={thBlank}>Сума податку (графа 6 або графа 7)<sup>7</sup></th>
              </tr>
              <tr>{['1', '2', '3', '4', '5', '6', '7', '8'].map((n) => <th key={n} style={thBlank}>{n}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const f = r.field;
                const none = r.rate == null;
                return (
                  <tr key={f}>
                    <td style={{ ...nameCell, fontSize: '0.72rem' }}>{r.title}</td>
                    <td style={numCell}>{r.row}</td>
                    <td style={valCell}><SumCell value={it.rows[`${f}G3`]} readOnly={!editable} print={print} label={`ПН ${r.row} дохід`} onChange={(raw) => setRow(i, `${f}G3`, raw)} /></td>
                    <td style={xCell}>{none ? 'Х' : r.rate}</td>
                    <td style={none ? xCell : valCell}>{none ? 'Х' : <SumCell value={it.rows[`${f}G5`]} readOnly={!editable} print={print} label={`ПН ${r.row} ставка за договором`} onChange={(raw) => setRow(i, `${f}G5`, raw)} />}</td>
                    <td style={none ? xCell : valCell}>{none ? 'Х' : <SumCell value={it.rows[`${f}G6`]} readOnly print={print} />}</td>
                    <td style={none ? xCell : valCell}>{none ? 'Х' : <SumCell value={it.rows[`${f}G7`]} readOnly={!editable} print={print} label={`ПН ${r.row} податок при негрошовій виплаті`} onChange={(raw) => setRow(i, `${f}G7`, raw)} />}</td>
                    <td style={none ? xCell : valCell}>{none ? 'Х' : <SumCell value={it.rows[`${f}G8`]} readOnly print={print} />}</td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 700 }}>
                <td style={nameCell}>Усього</td><td style={numCell}>33</td>
                <td style={valCell}><SumCell value={it.rows.R0133G3} readOnly print={print} /></td>
                <td style={xCell}>Х</td><td style={xCell}>Х</td>
                <td style={valCell}><SumCell value={it.rows.R0133G6} readOnly print={print} /></td>
                <td style={valCell}><SumCell value={it.rows.R0133G7} readOnly print={print} /></td>
                <td style={valCell}><SumCell value={it.rows.R0133G8} readOnly print={print} /></td>
              </tr>
            </tbody>
          </table>
          <div style={{ ...label, textAlign: 'center', fontWeight: 700, margin: '6px 0 2px' }}>
            Таблиця 2. Розрахунок прибутку від операцій з безпроцентними (дисконтними) облігаціями чи казначейськими зобовʼязаннями (рядок 18 таблиці 1)
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
            <thead>
              <tr>
                <th style={thBlank}>№ з/п</th><th style={thBlank}>Дата придбання цінних паперів</th><th style={thBlank}>Ціна придбання цінних паперів</th>
                <th style={thBlank}>Номінальна вартість цінних паперів</th><th style={thBlank}>Сума прибутку (графа 4 – графа 3)</th><th style={thBlank}>Сума податку (графа 5 × 18 / 100)</th>
                {editable && <th style={thBlank} />}
              </tr>
            </thead>
            <tbody>
              {(it.bonds ?? []).map((b, k) => (
                <tr key={k}>
                  <td style={numCell}>{k + 1}</td>
                  <td style={valCell}>{editable
                    ? <input type="date" value={b.date} aria-label={`Облігація ${k + 1} дата`} onChange={(e) => update(i, { bonds: it.bonds!.map((x, m) => (m === k ? { ...x, date: e.target.value } : x)) })}
                        style={{ border: 'none', background: '#fefce8', fontFamily: SERIF, width: '100%' }} />
                    : ro(b.date ? b.date.split('-').reverse().join('.') : '', 'center', print)}</td>
                  <td style={valCell}><SumCell value={b.price} readOnly={!editable} print={print} label={`Облігація ${k + 1} ціна`} onChange={(raw) => update(i, { bonds: it.bonds!.map((x, m) => (m === k ? { ...x, price: raw } : x)) })} /></td>
                  <td style={valCell}><SumCell value={b.nominal} readOnly={!editable} print={print} label={`Облігація ${k + 1} номінал`} onChange={(raw) => update(i, { bonds: it.bonds!.map((x, m) => (m === k ? { ...x, nominal: raw } : x)) })} /></td>
                  <td style={valCell}><SumCell value={b.profit} readOnly print={print} /></td>
                  <td style={valCell}><SumCell value={b.tax} readOnly print={print} /></td>
                  {editable && <td style={{ ...valCell, width: 30 }}><button type="button" onClick={() => update(i, { bonds: it.bonds!.filter((_, m) => m !== k) })}>×</button></td>}
                </tr>
              ))}
              <tr style={{ fontWeight: 700 }}>
                <td style={nameCell} colSpan={4}>Рядок 18 таблиці 1</td>
                <td style={valCell}><SumCell value={String((it.bonds ?? []).reduce((s, b) => s + Number(b.profit ?? 0), 0) || '')} readOnly print={print} /></td>
                <td style={valCell}><SumCell value={String((it.bonds ?? []).reduce((s, b) => s + Number(b.tax ?? 0), 0) || '')} readOnly print={print} /></td>
                {editable && <td style={valCell} />}
              </tr>
            </tbody>
          </table>
          {editable && <button type="button" style={{ marginTop: 4 }} onClick={() => update(i, { bonds: [...(it.bonds ?? []), { date: '', price: '', nominal: '' }] })}>+ облігація</button>}
          <div style={{ ...label, marginTop: 6 }}>
            <sup>6</sup> Міжнародний договір (назва і норма), за яким застосовано ставку:
            <TextCell value={it.treaty_text ?? ''} readOnly={!editable} print={print} label="Міжнародний договір" onChange={(v) => update(i, { treaty_text: v })} />
          </div>
          <div style={{ ...small, marginTop: 4 }}>
            <sup>1</sup> Як зазначено у контракті / договорі; назва і код країни — за Переліком кодів країн світу для статистичних цілей. <sup>2</sup> За наявності офшорного статусу.
            {' '}<sup>3</sup> Якщо нерезидент є утворенням без статусу юридичної особи. <sup>4</sup> При застосуванні міжнародного договору з країною бенефіціарного отримувача.
            {' '}<sup>5</sup> Додаток заповнюється окремо по кожному нерезиденту. <sup>7</sup> Значення графи 8 рядка 33 переноситься до рядка 23 ПН декларації.
          </div>
          {doc && <SignaturesBlock doc={doc} org={org} print={print} withInn={false} />}
        </div>
      ))}
      {editable && <button type="button" onClick={() => setItems?.([...items, { ...EMPTY_PN, rows: {} }])}>+ додати нерезидента</button>}
    </div>
  );
}

/* ----------------------------- ВП ----------------------------- */

const VP_MAIN_FIELDS = new Set([
  'R001G3', 'R002G3', 'R0021G3', 'R003G3', 'R004G3', 'R005G3', 'R006G3', 'R0061G3', 'R0062G3', 'R0063G3', 'R0064G3',
  'R00641G3', 'R00642G3', 'R007G3', 'R0071G3', 'R0072G3', 'R008G3', 'R009G3', 'R0091G3', 'R00911G3', 'R00912G3', 'R0092G3',
  'R010G3', 'R011G3', 'R012G3', 'R013G3', 'R014G3', 'R015G3',
  'R016G3', 'R017G3', 'R018G3', 'R019G3', 'R020G3', 'R021G3', 'R022G3', 'R023G3', 'R024G3', 'R025G3',
  'R026G3', 'R027G3', 'R028G3', 'R042G3',
]);
const HF: { code: string; label: string }[] = [
  { code: 'HF1', label: 'Баланс (Звіт про фінансовий стан)' }, { code: 'HF2', label: 'Звіт про фінансові результати (Звіт про сукупний дохід)' },
  { code: 'HF3', label: 'Звіт про рух грошових коштів' }, { code: 'HF4', label: 'Звіт про власний капітал' }, { code: 'HF5', label: 'Примітки до річної фінансової звітності' },
  { code: 'HF6', label: 'Фінансовий звіт субʼєкта малого підприємництва: Баланс' }, { code: 'HF7', label: 'Фінансовий звіт субʼєкта малого підприємництва: Звіт про фінансові результати' },
  { code: 'HF8', label: 'Спрощений фінансовий звіт: Баланс' }, { code: 'HF9', label: 'Спрощений фінансовий звіт: Звіт про фінансові результати' },
];

export function VpAppendix({ resultRows, items, setItems, readOnly, print, year, quarter, onLoadFiled, doc, org }: {
  resultRows: AppendixRowDef[]; items: VpItem[];
  setItems?: (items: VpItem[]) => void; readOnly?: boolean; print?: boolean;
  year: number; quarter: number;
  /** підтягнути подану декларацію періоду (сервер) */
  onLoadFiled?: (i: number) => void;
  doc?: ProfitTaxDeclaration; org?: Organization;
}) {
  const editable = !print && !readOnly && !!setItems;
  const update = (i: number, patch: Partial<VpItem>) => setItems?.(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const setVal = (i: number, code: string, raw: string, key: 'values' | 'results') => {
    const next = { ...(items[i]?.[key] ?? {}) };
    if (raw === '' || Number(raw) === 0) delete next[code]; else next[code] = raw;
    update(i, { [key]: next } as Partial<VpItem>);
  };
  const mainRows = MAIN_ROWS.filter((r) => VP_MAIN_FIELDS.has(r.field));
  const prevPeriods: { y: number; q: number }[] = [];
  for (let y = year - 3; y <= year; y++) for (let q = 1; q <= 4; q++) if (y < year || q < quarter) prevPeriods.push({ y, q });
  return (
    <div>
      {items.length === 0 && <div style={{ ...label, color: '#555', marginBottom: 8 }}>Періодів для виправлення не додано — додаток ВП не подається.</div>}
      {items.map((it, i) => (
        <div key={i} className={print && i ? 'print-page' : undefined} style={{ marginBottom: '1rem' }}>
          {doc && (
            <AppendixHeader doc={doc} org={org} print={print} noCorrectingMark corrected={{ year: it.year, quarter: it.quarter }}
              title={`Додаток ВП до Податкової декларації з податку на прибуток підприємств (рядки 29-32, 34-36, 38-40, 42)${items.length > 1 ? ` (№ ${i + 1})` : ''}`}
              subtitle="Розрахунок податкових зобовʼязань за період, у якому виявлено помилку(и)" />
          )}
          {editable && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={label}>Уточнюваний період:</span>
              <select value={`${it.year}-${it.quarter}`} aria-label="Уточнюваний період"
                onChange={(e) => { const [y, q] = e.target.value.split('-').map(Number); update(i, { year: y, quarter: q, values: {}, filed: undefined, filed_number: undefined, hf: undefined }); }}>
                {prevPeriods.map((p) => <option key={`${p.y}-${p.q}`} value={`${p.y}-${p.q}`}>{PERIODS[p.q]} {p.y}</option>)}
              </select>
              {onLoadFiled && <button type="button" onClick={() => onLoadFiled(i)} title="Підтягнути рядки поданої декларації цього періоду як початкові">з поданої декларації</button>}
              {it.filed_number && <span style={small}>подана: {it.filed_number}</span>}
              {it.filed_missing && <span style={{ ...small, color: '#b45309' }}>поданої декларації за цей період у сервісі немає — різниця рахується від нуля</span>}
              <button type="button" style={{ marginLeft: 'auto' }} onClick={() => setItems?.(items.filter((_, j) => j !== i))}>прибрати період</button>
            </div>
          )}
          {!doc && <div style={label}><PeriodMarks quarter={it.quarter} year={it.year} /></div>}
          <div style={{ ...label, textAlign: 'center', fontWeight: 700, margin: '6px 0 2px' }}>
            Таблиця 1. Врахування помилки(ок) у звітному (податковому) періоді, наступному за періодом, за який виявлено помилку
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
            <colgroup><col /><col style={{ width: 70 }} /><col style={{ width: 140 }} /></colgroup>
            <thead>
              <tr><th style={{ ...thBlank, textAlign: 'right', border: 'none' }} colSpan={3}>(грн)</th></tr>
              <tr><th style={thBlank}>ПОКАЗНИКИ</th><th style={thBlank}>Код рядка</th><th style={thBlank}>Сума</th></tr>
            </thead>
            <tbody>
              {mainRows.map((r) => (
                <tr key={r.field} style={{ fontWeight: r.auto ? 700 : 400 }}>
                  <td style={{ ...nameCell, fontSize: '0.74rem' }}>{withRates(r.title, it.values)}</td>
                  <td style={numCell}>{r.row}</td>
                  <td style={valCell}>
                    <SumCell value={it.values[r.field]} readOnly={!editable || !!r.auto} print={print} label={`ВП ${r.row}`} onChange={(raw) => setVal(i, r.field, raw, 'values')} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ ...label, textAlign: 'center', fontWeight: 700, margin: '6px 0 2px' }}>Таблиця 2. Результати виправлення помилок(ки)</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
            <colgroup><col /><col style={{ width: 70 }} /><col style={{ width: 140 }} /></colgroup>
            <thead><tr><th style={thBlank}>ПОКАЗНИКИ</th><th style={thBlank}>Код рядка</th><th style={thBlank}>Сума</th></tr></thead>
            <tbody>
              <tr><td colSpan={3} style={{ ...nameCell, textAlign: 'center', fontWeight: 700 }}>Виправлення помилок</td></tr>
              {resultRows.map((r, k) => (
                <>
                  {k === 4 && <tr key="h2"><td colSpan={3} style={{ ...nameCell, textAlign: 'center', fontWeight: 700 }}>Виправлення помилок з податку на прибуток, який утримується при виплаті доходів (прибутків) нерезидентів</td></tr>}
                  {k === 7 && <tr key="h3"><td colSpan={3} style={{ ...nameCell, textAlign: 'center', fontWeight: 700 }}>Виправлення помилок щодо сум авансових внесків з пунктів обміну іноземних валют, з місць роздрібної торгівлі пальним</td></tr>}
                  <tr key={r.field} style={{ fontWeight: r.auto ? 700 : 400 }}>
                    <td style={{ ...nameCell, fontSize: '0.74rem' }}>{r.title}</td>
                    <td style={numCell}>{r.row}</td>
                    <td style={valCell}>
                      <SumCell value={it.results?.[r.field]} readOnly={!editable || !!r.auto} print={print} label={`ВП результат ${r.row}`} onChange={(raw) => setVal(i, r.field, raw, 'results')} />
                    </td>
                  </tr>
                </>
              ))}
              {it.filed && Object.keys(it.filed).length > 0 && !print && (
                <tr><td colSpan={3} style={{ ...nameCell, ...small }}>
                  Подана декларація: рядок 19 — {fmt0(it.filed.R019G3) || '0'}, 22 — {fmt0(it.filed.R022G3) || '0'}, 25 — {fmt0(it.filed.R025G3) || '0'}, 28 — {fmt0(it.filed.R028G3) || '0'}, 42 — {fmt0(it.filed.R042G3) || '0'}
                </td></tr>
              )}
            </tbody>
          </table>
          <div style={{ ...label, textAlign: 'center', fontWeight: 700, margin: '6px 0 2px' }}>
            Таблиця 3. Наявність поданих до Податкової декларації додатків — форм фінансової звітності за звітний (податковий) період, що уточнюється
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
            <tbody>
              <tr>
                {HF.map((h) => (
                  <td key={h.code} style={{ ...nameCell, textAlign: 'center', fontSize: '0.66rem', verticalAlign: 'bottom' }}>
                    {h.label}<br />
                    <Mark on={(it.hf ?? []).includes(h.code)} readOnly={!editable} title={h.code}
                      onToggle={() => { const cur = new Set(it.hf ?? []); if (cur.has(h.code)) cur.delete(h.code); else cur.add(h.code); update(i, { hf: Array.from(cur) }); }} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          {doc && <SignaturesBlock doc={doc} org={org} print={print} withInn={false} />}
        </div>
      ))}
      {editable && (
        <button type="button" onClick={() => {
          const last = prevPeriods[prevPeriods.length - 1];
          if (last) setItems?.([...items, { year: last.y, quarter: last.q, values: {} }]);
        }}>+ додати період для виправлення</button>
      )}
    </div>
  );
}

export { XMark };
